import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "../ui/sonner";
import { confirmBbLink, exchangeBbHandoff, formatApiErrorDetail, getBbPendingLink } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

// Bankroll Builder (separate iOS app) -> EdgeBall account linking, mounted once at the app
// root. Two independent jobs, both driven purely by server state (never by comparing emails):
// 1. On first mount, if the URL has ?bb_code=... (BB's app opened EdgeBall with it), redeem it
//    immediately server-side (the code is single-use and expires in 60s) and strip it from the
//    URL. This works whether the visitor is logged in yet or not.
// 2. Whenever the visitor is authenticated, ask the backend if a redeemed-but-unconfirmed link
//    is waiting (the backend only ever says yes/no -- it never hands the BB identity to the
//    browser) and, if so, show this explicit confirm/decline prompt before attaching it.
export const BbLinkHandler = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(null); // null = nothing to show, {is_premium} = show prompt
  const [confirming, setConfirming] = useState(false);
  const exchangedRef = useRef(false);
  const checkedForUserRef = useRef(null);

  const checkPending = useCallback(() => {
    getBbPendingLink()
      .then((d) => {
        if (d.pending) setPending({ is_premium: d.is_premium });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (exchangedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("bb_code");
    if (!code) return;
    exchangedRef.current = true;
    params.delete("bb_code");
    const cleanSearch = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`);
    exchangeBbHandoff(code)
      .then(() => {
        toast.success("Bankroll Builder verified -- log in or create an account to finish linking it.");
        // The visitor may already be logged in (e.g. re-opening the link in an existing
        // session): re-check right away instead of waiting on the user-change effect below,
        // which may have already run (and skipped, via checkedForUserRef) before this slower
        // server-to-server exchange call finished.
        checkPending();
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not verify the Bankroll Builder link");
      });
  }, [checkPending]);

  useEffect(() => {
    if (!user || checkedForUserRef.current === user.id) return;
    checkedForUserRef.current = user.id;
    checkPending();
  }, [user, checkPending]);

  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      await confirmBbLink();
      toast.success("Bankroll Builder Premium linked to this EdgeBall account.");
      setPending(null);
      await refresh();
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not link Bankroll Builder");
      setPending(null);
    } finally {
      setConfirming(false);
    }
  }, [refresh, navigate]);

  return (
    <AlertDialog open={!!pending && !!user} onOpenChange={(open) => !open && setPending(null)}>
      <AlertDialogContent data-testid="bb-link-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldCheck size={20} weight="fill" className="text-eb-accent" /> Link Bankroll Builder Premium?
          </AlertDialogTitle>
          <AlertDialogDescription>
            We verified an active Bankroll Builder subscription on your Apple account. Link it to this EdgeBall
            account to get Premium access here too, without paying again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="bb-link-decline-button" disabled={confirming}>
            Not now
          </AlertDialogCancel>
          <AlertDialogAction data-testid="bb-link-confirm-button" onClick={handleConfirm} disabled={confirming}>
            {confirming ? "Linking..." : "Yes, link it"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
